import React, { useEffect, useState } from 'react';
import { Plane, Bus, Upload } from 'lucide-react';
import api from '../api';
import { toast } from 'react-hot-toast';
import countryList from 'react-select-country-list';


const Step1 = ({ formData, setFormData, formErrors, handleChange, handleFileUpload }) => {
    const [isLoading, setIsLoading] = useState(false);
    const countryOptions = countryList().getData();

    useEffect(() => {
        if (formData.document_voyage.type) {
            fetchDernierDocument(formData.document_voyage.type);
        }
    }, [formData.document_voyage.type]);

    const fetchDernierDocument = async (type_document) => {
        setIsLoading(true);
        try {
            const response = await api.get(`/voyage/dernier-document/${type_document}/`);
            if (response.data) {
                setFormData(prev => ({
                    ...prev,
                    document_voyage: {
                        ...prev.document_voyage,
                        ...response.data
                    }
                }));
                toast.success('Document pré-rempli avec succès !');
            }
        } catch (error) {
            console.error('Erreur lors de la récupération du document:', error);
            toast.error('Aucun document valide trouvé. Veuillez remplir les champs.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="border-b border-gray-200 pb-4">
                <h2 className="text-xl font-bold text-[#287094]">Informations sur le voyage</h2>
                <p className="text-sm text-gray-600">Sélectionnez votre type de transport et complétez les informations</p>
                <p className="text-xs text-red-500 mt-1">Les champs marqués * sont obligatoires</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-[#287094] mb-6">Type de transport</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                        type="button"
                        onClick={() => setFormData({ ...formData, type_voyage: 'aerien' })}
                        className={`p-6 rounded-lg border-2 transition-all ${formData.type_voyage === 'aerien'
                            ? 'border-[#96B734] bg-[#F4F8E9]'
                            : 'border-gray-200 hover:border-[#96B734]'
                            }`}
                    >
                        <div className="flex flex-col items-center space-y-3">
                            <Plane className={`w-12 h-12 ${formData.type_voyage === 'aerien' ? 'text-[#96B734]' : 'text-gray-400'}`} />
                            <span className={`font-medium ${formData.type_voyage === 'aerien' ? 'text-[#96B734]' : 'text-gray-600'}`}>
                                Transport Aérien
                            </span>
                        </div>
                    </button>

                    <button
                        type="button"
                        onClick={() => setFormData({ ...formData, type_voyage: 'terrestre' })}
                        className={`p-6 rounded-lg border-2 transition-all ${formData.type_voyage === 'terrestre'
                            ? 'border-[#96B734] bg-[#F4F8E9]'
                            : 'border-gray-200 hover:border-[#96B734]'
                            }`}
                    >
                        <div className="flex flex-col items-center space-y-3">
                            <Bus className={`w-12 h-12 ${formData.type_voyage === 'terrestre' ? 'text-[#96B734]' : 'text-gray-400'}`} />
                            <span className={`font-medium ${formData.type_voyage === 'terrestre' ? 'text-[#96B734]' : 'text-gray-600'}`}>
                                Transport Terrestre
                            </span>
                        </div>
                    </button>
                </div>
            </div>

            {formData.type_voyage && (
                <>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h4 className="text-md font-semibold text-[#287094] mb-6">Document d'identité</h4>
                        {isLoading ? (
                            <div className="flex justify-center items-center">
                                <div className="w-8 h-8 border-t-2 border-b-2 border-[#96B734] rounded-full animate-spin"></div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        TYPE DE DOCUMENT <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="document_voyage.type"
                                        value={formData.document_voyage.type}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    >
                                        <option value="">Sélectionnez le type</option>
                                        <option value="Passeport">Passeport</option>
                                        <option value="CNI">Carte Nationale d'Identité</option>
                                    </select>
                                    {formErrors.document_type && (
                                        <p className="text-xs text-red-500 mt-1">{formErrors.document_type}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        NUMÉRO DU DOCUMENT <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="document_voyage.numero"
                                        value={formData.document_voyage.numero}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    />
                                    {formErrors.document_numero && (
                                        <p className="text-xs text-red-500 mt-1">{formErrors.document_numero}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        DATE D'EXPIRATION <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="document_voyage.date_expiration"
                                        value={formData.document_voyage.date_expiration}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    />
                                    {formErrors.document_date && (
                                        <p className="text-xs text-red-500 mt-1">{formErrors.document_date}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        TÉLÉCHARGEZ VOTRE DOCUMENT <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex items-center space-x-3">
                                        <label className="flex items-center px-4 py-2 border border-[#96B734] rounded-lg cursor-pointer hover:bg-[#F4F8E9] transition-colors">
                                            <Upload className="w-4 h-4 text-[#96B734] mr-2" />
                                            <span className="text-sm text-[#437011]">Choisir un fichier</span>
                                            <input
                                                type="file"
                                                name="document_voyage.fichier"
                                                className="hidden"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={handleFileUpload}
                                                required={!formData.document_voyage.fichier}
                                            />
                                        </label>
                                        {formData.document_voyage.fichier && (
                                            <span className="text-sm text-gray-600">
                                                {formData.document_voyage.fichier.name}
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">PDF, PNG, JPG jusqu'à 10MB</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        PAYS ÉMETTEUR <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="document_voyage.pays_emeteur"
                                        value={formData.document_voyage.pays_emeteur}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    >
                                        <option value="">Sélectionnez le pays</option>
                                        {countryOptions.map(country => (
                                            <option key={country.value} value={country.value}>
                                                {country.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        DÉLIVRÉ PAR <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="document_voyage.delivre_par"
                                        value={formData.document_voyage.delivre_par}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h4 className="text-md font-semibold text-[#287094] mb-6">Informations sur le séjour</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    MOTIF <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="sejour.motif"
                                    value={formData.sejour.motif}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                    required
                                >
                                    <option value="">Sélectionnez un motif</option>
                                    <option value="Tourisme">Tourisme</option>
                                    <option value="Affaires">Affaires</option>
                                    <option value="Famille">Visite Famille</option>
                                    <option value="Etudes">Études</option>
                                    <option value="Santé">Santé</option>
                                    <option value="Autre">Autre</option>
                                </select>
                            </div>

                            {formData.sejour.motif === 'Autre' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        PRÉCISEZ LE MOTIF <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="sejour.autre_motif"
                                        value={formData.sejour.autre_motif}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    DURÉE DU SÉJOUR (en jour)<span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    name="sejour.duree"
                                    value={formData.sejour.duree}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    VILLE DE DÉPART <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="sejour.ville_depart"
                                    value={formData.sejour.ville_depart}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    VILLE D'ARRIVÉE <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="sejour.ville_arrivee"
                                    value={formData.sejour.ville_arrivee}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                    required
                                >
                                    <option value="">Sélectionnez une ville</option>
                                    <option value="Niamey">Niamey</option>
                                    <option value="Maradi">Maradi</option>
                                    <option value="Tahoua">Tahoua</option>
                                    <option value="Dosso">Dosso</option>
                                    <option value="Tillabery">Tillabery</option>
                                    <option value="Diffa">Diffa</option>
                                    <option value="Agadez">Agadez</option>
                                    <option value="Zinder">Zinder</option>
                                </select>
                               
                            </div>
                        </div>
                    </div>

                    {formData.type_voyage === 'aerien' ? (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h4 className="text-md font-semibold text-[#287094] mb-6">Transport Aérien</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        COMPAGNIE AÉRIENNE <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="sejour.compagnie_aerienne"
                                        value={formData.sejour.compagnie_aerienne}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    >
                                        <option value="">Sélectionnez une compagnie</option>
                                        <option value="Air Senegal">Air Senegal</option>
                                        <option value="Ethiopian">Ethiopian Airlines</option>
                                        <option value="RwandAir">RwandAir</option>
                                        <option value="autre">Autre compagnie</option>
                                    </select>
                                    {formErrors.compagnie_aerienne && (
                                        <p className="text-xs text-red-500 mt-1">{formErrors.compagnie_aerienne}</p>
                                    )}
                                </div>

                                {formData.sejour.compagnie_aerienne === 'autre' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            PRÉCISEZ LA COMPAGNIE <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="sejour.autre_compagnie"
                                            value={formData.sejour.autre_compagnie}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                            required
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        AÉROPORT DE DÉPART <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="sejour.aeroport_depart"
                                        value={formData.sejour.aeroport_depart}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        placeholder="Ex: London Heathrow"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        AÉROPORT D'ARRIVÉE <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="sejour.aeroport_arrivee"
                                        value={formData.sejour.aeroport_arrivee}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        placeholder="Ex: Aéroport International Diori Hamani"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        NUMÉRO DE VOL <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="sejour.numero_vol"
                                        value={formData.sejour.numero_vol}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        placeholder="Ex: AE123"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        DATE DU VOL <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="sejour.date_vol"
                                        value={formData.sejour.date_vol}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                       
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        HEURE DU VOL <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="time"
                                        name="sejour.heure_vol"
                                        value={formData.sejour.heure_vol}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h4 className="text-md font-semibold text-[#287094] mb-6">Transport Terrestre</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        COMPAGNIE DE TRANSPORT <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="sejour.compagnie_terrestre"
                                        value={formData.sejour.compagnie_terrestre}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    >
                                        <option value="">Sélectionnez une compagnie</option>
                                        <option value="rimbo">Rimbo Transport</option>
                                        <option value="stm">STM</option>
                                        <option value="autre">Autre compagnie</option>
                                    </select>
                                </div>

                                {formData.sejour.compagnie_terrestre === 'autre' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            PRÉCISEZ LA COMPAGNIE <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="sejour.autre_compagnie_terrestre"
                                            value={formData.sejour.autre_compagnie_terrestre}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                            required
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        TYPE DE VÉHICULE <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="sejour.type_vehicule"
                                        value={formData.sejour.type_vehicule}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    >
                                        <option value="">Sélectionnez le type</option>
                                        <option value="bus">Bus</option>
                                        <option value="minibus">Minibus</option>
                                        <option value="voiture">Voiture</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        NUMÉRO DU VÉHICULE <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="sejour.numero_vehicule"
                                        value={formData.sejour.numero_vehicule}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        placeholder="Ex: AB 1234 NE"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        VILLE DE DÉPART <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="sejour.ville_depart"
                                        value={formData.sejour.ville_depart}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        VILLE D'ARRIVÉE <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="sejour.ville_arrivee"
                                        value={formData.sejour.ville_arrivee}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        DATE DE DÉPART <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="sejour.date_depart"
                                        value={formData.sejour.date_depart}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#96B734]"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Step1;
